import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

export default function AddSkillModal({ isOpen, onClose, onSave, skill }) {
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    subtopics: [{
      id: crypto.randomUUID(),
      name: '',
      progress: 0,
      status: 'not_started', // not_started, in_progress, completed
      priority: 'medium', // low, medium, high
      estimatedHours: 0,
      resources: [],
      lastStudied: null,
      notes: '',
      retentionLevel: 'LEARNING',
      reviewHistory: []
    }],
    notes: ''
  })

  useEffect(() => {
    if (skill) {
      setFormData({
        name: skill.name || '',
        category: skill.category || '',
        description: skill.description || '',
        subtopics: skill.subtopics?.map(st => ({
          ...st,
          status: st.status || 'not_started',
          priority: st.priority || 'medium',
          estimatedHours: st.estimatedHours || 0,
          resources: st.resources || [],
          lastStudied: st.lastStudied || null,
          notes: st.notes || '',
          retentionLevel: st.retentionLevel || 'LEARNING',
          reviewHistory: st.reviewHistory || []
        })) || [{ id: crypto.randomUUID(), name: '', progress: 0, status: 'not_started', priority: 'medium', estimatedHours: 0, resources: [], notes: '', retentionLevel: 'LEARNING', reviewHistory: [] }],
        notes: skill.notes || ''
      })
    }
  }, [skill])

  const handleSubmit = (e) => {
    e.preventDefault()
    if (skill) {
      onSave(skill.id, {
        ...formData,
        updatedAt: new Date().toISOString()
      })
    } else {
      onSave({
        ...formData,
        createdAt: new Date().toISOString()
      })
    }
    onClose()
  }

  const addSubtopic = () => {
    setFormData(prev => ({
      ...prev,
      subtopics: [...prev.subtopics, {
        id: crypto.randomUUID(),
        name: '',
        progress: 0,
        status: 'not_started',
        priority: 'medium',
        estimatedHours: 0,
        resources: [],
        lastStudied: null,
        notes: '',
        retentionLevel: 'LEARNING',
        reviewHistory: []
      }]
    }))
  }

  const removeSubtopic = (index) => {
    setFormData(prev => ({
      ...prev,
      subtopics: prev.subtopics.filter((_, i) => i !== index)
    }))
  }

  const updateSubtopic = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      subtopics: prev.subtopics.map((subtopic, i) =>
        i === index ? { ...subtopic, [field]: value } : subtopic
      )
    }))
  }

  const addResource = (index) => {
    setFormData(prev => ({
      ...prev,
      subtopics: prev.subtopics.map((subtopic, i) =>
        i === index ? {
          ...subtopic,
          resources: [...subtopic.resources, { url: '', title: '', type: 'link' }]
        } : subtopic
      )
    }))
  }

  const removeResource = (subtopicIndex, resourceIndex) => {
    setFormData(prev => ({
      ...prev,
      subtopics: prev.subtopics.map((subtopic, i) =>
        i === subtopicIndex ? {
          ...subtopic,
          resources: subtopic.resources.filter((_, j) => j !== resourceIndex)
        } : subtopic
      )
    }))
  }

  const updateResource = (subtopicIndex, resourceIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      subtopics: prev.subtopics.map((subtopic, i) =>
        i === subtopicIndex ? {
          ...subtopic,
          resources: subtopic.resources.map((resource, j) =>
            j === resourceIndex ? { ...resource, [field]: value } : resource
          )
        } : subtopic
      )
    }))
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-soft max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto"
        >
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {skill ? 'Edit Skill' : 'Add New Skill'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Skill Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category
                </label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows="2"
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
              />
            </div>

            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Subtopics</h3>
                <button
                  type="button"
                  onClick={addSubtopic}
                  className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                >
                  + Add Subtopic
                </button>
              </div>

              {formData.subtopics.map((subtopic, index) => (
                <div key={index} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg space-y-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 space-y-4">
                      <div>
                        <input
                          type="text"
                          value={subtopic.name}
                          onChange={(e) => updateSubtopic(index, 'name', e.target.value)}
                          placeholder="Subtopic name"
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                          required
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Status
                          </label>
                          <select
                            value={subtopic.status}
                            onChange={(e) => updateSubtopic(index, 'status', e.target.value)}
                            className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                          >
                            <option value="not_started">Not Started</option>
                            <option value="in_progress">In Progress</option>
                            <option value="completed">Completed</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Priority
                          </label>
                          <select
                            value={subtopic.priority}
                            onChange={(e) => updateSubtopic(index, 'priority', e.target.value)}
                            className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                          >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Estimated Hours
                          </label>
                          <input
                            type="number"
                            value={subtopic.estimatedHours}
                            onChange={(e) => updateSubtopic(index, 'estimatedHours', parseInt(e.target.value))}
                            min="0"
                            className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Progress
                        </label>
                        <div className="flex items-center gap-4">
                          <input
                            type="range"
                            value={subtopic.progress}
                            onChange={(e) => updateSubtopic(index, 'progress', parseInt(e.target.value))}
                            min="0"
                            max="100"
                            className="flex-1"
                          />
                          <span className="text-sm text-gray-600 dark:text-gray-400 w-12 text-right">
                            {subtopic.progress}%
                          </span>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Notes
                        </label>
                        <textarea
                          value={subtopic.notes}
                          onChange={(e) => updateSubtopic(index, 'notes', e.target.value)}
                          rows="3"
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                          placeholder="Add notes about this subtopic..."
                        />
                      </div>

                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Learning Resources
                          </label>
                          <button
                            type="button"
                            onClick={() => addResource(index)}
                            className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                          >
                            + Add Resource
                          </button>
                        </div>
                        <div className="space-y-2">
                          {subtopic.resources.map((resource, resourceIndex) => (
                            <div key={resourceIndex} className="flex gap-2">
                              <input
                                type="text"
                                value={resource.title}
                                onChange={(e) => updateResource(index, resourceIndex, 'title', e.target.value)}
                                placeholder="Resource title"
                                className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                              />
                              <input
                                type="text"
                                value={resource.url}
                                onChange={(e) => updateResource(index, resourceIndex, 'url', e.target.value)}
                                placeholder="URL"
                                className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                              />
                              <button
                                type="button"
                                onClick={() => removeResource(index, resourceIndex)}
                                className="p-2 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                              >
                                🗑️
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {formData.subtopics.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSubtopic(index)}
                        className="p-2 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 ml-4"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                General Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows="3"
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                placeholder="Add general notes about this skill..."
              />
            </div>

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
              >
                {skill ? 'Update' : 'Add'} Skill
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}
